import { useState, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import { ReviewRequestForm, ReviewRequestFormRef } from "@/components/ReviewRequestForm";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { supabase } from "@/lib/supabase";
import { Mail, Eye, AlertTriangle } from "lucide-react";
import { getPlanLimits } from "@shared/plans";
import type { Staff, Business } from "@shared/schema";

export default function SendRequest() {
  const { toast } = useToast();
  const formRef = useRef<ReviewRequestFormRef>(null);

  const { data: business } = useQuery<Business>({
    queryKey: ["/api/business"],
  });

  const { data: staffList, isLoading } = useQuery<Staff[]>({
    queryKey: ["/api/staff"],
  });

  // Upload photo to Supabase Storage and return public URL
  const uploadPhoto = async (file: File, businessId: string): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${businessId}/${Date.now()}.${fileExt}`;
      
      const { error } = await supabase.storage
        .from('job-photos')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) {
        console.error('Photo upload error:', error);
        return null;
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('job-photos')
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (err) {
      console.error('Photo upload failed:', err);
      return null;
    }
  };

  const sendRequestMutation = useMutation({
    mutationFn: async (data: {
      staffId: string;
      customerName: string;
      customerEmail: string;
      jobDescription?: string;
      photoFile?: File;
    }) => {
      // Upload photo if provided
      let jobPhotoUrl: string | null = null;
      if (data.photoFile && business?.id) {
        jobPhotoUrl = await uploadPhoto(data.photoFile, business.id);
      }

      return apiRequest("POST", "/api/review-requests", {
        staffId: data.staffId,
        customerName: data.customerName,
        customerEmail: data.customerEmail,
        jobDescription: data.jobDescription,
        jobPhotoUrl: jobPhotoUrl,
      });
    },
    onSuccess: (_, variables) => {
      const staffMember = staffList?.find((s) => s.id === variables.staffId);
      queryClient.invalidateQueries({ queryKey: ["/api/review-requests"] });
      queryClient.invalidateQueries({ queryKey: ["/api/business/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/business"] });
      toast({
        title: "Review request sent!",
        description: `Email sent to ${variables.customerEmail} for ${staffMember?.name}`,
      });
      // Clear the form after successful submission
      formRef.current?.reset();
    },
    onError: (error: Error) => {
      // Try to extract the specific error message from the API response
      let errorMessage = "Failed to send review request";
      try {
        // Error format is "400: {JSON}" - extract and parse the JSON
        const match = error.message.match(/^\d+:\s*(.+)$/);
        if (match) {
          const parsed = JSON.parse(match[1]);
          if (parsed.error?.message) {
            errorMessage = parsed.error.message;
          } else if (parsed.error?.details?.[0]?.message) {
            errorMessage = parsed.error.details[0].message;
          }
        }
      } catch {
        // Use default message if parsing fails
      }
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = async (data: {
    staffId: string;
    customerName: string;
    customerEmail: string;
    jobDescription?: string;
    photoFile?: File;
  }) => {
    sendRequestMutation.mutate(data);
  };

  const staffMembers = (staffList || [])
    .filter((s) => s.isActive)
    .map((s) => ({ id: s.id, name: s.name }));

  // Calculate remaining requests for plan limit warning
  const planLimits = business ? getPlanLimits(business.planType || "free") : null;
  const monthlyUsed = business?.monthlyRequestCount || 0;
  const monthlyLimit = planLimits?.maxRequestsPerMonth || 50;
  const remaining = monthlyLimit === Infinity ? Infinity : Math.max(0, monthlyLimit - monthlyUsed);
  const isAtLimit = remaining === 0;
  const isApproachingLimit = remaining !== Infinity && remaining <= 5 && remaining > 0;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold">Send Review Request</h1>
        <p className="text-muted-foreground mt-1">
          Request a review from a customer via email
        </p>
      </div>

      {/* Plan limit warning */}
      {isAtLimit && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Monthly limit reached</AlertTitle>
          <AlertDescription className="flex items-center justify-between">
            <span>
              You've used all {monthlyLimit} review requests for this month. 
              Upgrade your plan to send more.
            </span>
            <Button variant="outline" size="sm" asChild className="ml-4">
              <Link href="/pricing">Upgrade</Link>
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isApproachingLimit && (
        <Alert className="mb-6 border-yellow-500/50 bg-yellow-500/10">
          <AlertTriangle className="h-4 w-4 text-yellow-600" />
          <AlertTitle className="text-yellow-600">Approaching limit</AlertTitle>
          <AlertDescription>
            You have {remaining} review request{remaining !== 1 ? "s" : ""} remaining this month 
            ({monthlyUsed} of {monthlyLimit} used).
            {" "}
            <Link href="/pricing" className="text-primary hover:underline font-medium">
              Upgrade for unlimited requests
            </Link>
          </AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          {isLoading ? (
            <Skeleton className="h-96" />
          ) : isAtLimit ? (
            <Card className="border-destructive/50">
              <CardContent className="pt-6 text-center">
                <AlertTriangle className="h-12 w-12 mx-auto text-destructive mb-4" />
                <h3 className="text-lg font-semibold mb-2">Monthly Limit Reached</h3>
                <p className="text-muted-foreground mb-4">
                  You've sent {monthlyUsed} of {monthlyLimit} review requests this month.
                  Upgrade your plan to continue sending requests.
                </p>
                <Button asChild>
                  <Link href="/pricing">View Plans</Link>
                </Button>
              </CardContent>
            </Card>
          ) : (
            <ReviewRequestForm
              ref={formRef}
              staffMembers={staffMembers}
              onSubmit={handleSubmit}
              isSubmitting={sendRequestMutation.isPending}
            />
          )}
        </div>
        
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Eye className="h-5 w-5" />
                Email Preview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-md p-4 bg-muted/30">
                <div className="flex items-center gap-2 mb-4 pb-4 border-b">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium">Review Request</p>
                    <p className="text-xs text-muted-foreground">
                      from {business?.name || "Your Business"}
                    </p>
                  </div>
                </div>
                <div className="space-y-4 text-sm">
                  {business?.showLogoInEmail && business?.logoUrl ? (
                    <div className="text-center mb-4">
                      <img 
                        src={business.logoUrl} 
                        alt={business.name}
                        className="h-12 max-w-[150px] object-contain mx-auto"
                      />
                    </div>
                  ) : (
                    <p className="font-semibold text-base">{business?.name || "Your Business"}</p>
                  )}
                  <p>Hi [Customer Name],</p>
                  <p>
                    Thank you for choosing {business?.name || "our business"}! We hope you
                    were satisfied with the service provided by [Staff Name].
                  </p>
                  <p>
                    We'd greatly appreciate it if you could take a moment to
                    share your experience. Your feedback helps us improve and
                    helps other customers find quality service.
                  </p>
                  <div className="bg-primary/10 rounded-md p-4 text-center">
                    <p className="font-medium mb-2">Rate Your Experience</p>
                    <div className="flex justify-center gap-1 text-2xl">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span key={star} className="text-yellow-400">
                          {String.fromCharCode(9733)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-muted-foreground text-xs">
                    {business?.name || "Your Business"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
